<script setup>
import { ELearning } from '@pfsc/core/models/e-learning';
import { LICENSE_STATUS, LicenseStatus } from '@pfsc/core/models/license-status';
import { PACKAGE_TYPE } from '@pfsc/core/models/package-type';
import { DEFAULT_PAGINATION_INFO, PaginationInfo } from '@pfsc/core/models/pagination-info';
import {
  DEFAULT_QUERY_PARAMS,
  DEFAULT_QUERY_PARAMS_WITH_SORT, QueryParams, mapModelToTableSort, mapTableSortToModel,
} from '@pfsc/core/models/query-params';
import { getFullName } from '@pfsc/core/models/user';
import { ELearningRegistrationOptions, eLearningService } from '@pfsc/core/services/e-learning.service';
import { DEFAULT_TABLE_QUERY_PARAMS } from '@pfsc/core/services/table-query-params.service';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { useTriggerStore } from '@pfsc/core/stores/trigger.store';
import { notificationService } from '@pfsc/core/ui-services/notification.service';
import { queryParamsService } from '@pfsc/core/ui-services/query-params.service';
import { TAB_VALUE } from '@pfsc/core/ui-services/tabs.service';
import { dateUtils } from '@pfsc/core/utils/date';
import { toReadableIfNull } from '@pfsc/core/utils/toReadableIfNull';
import { toggleExecutionState } from '@pfsc/core/utils/toggleExecutionState';
import moment from 'moment';
import {
  computed, onMounted,
  ref, watch,
} from 'vue';
import { redirectUtils } from '@pfsc/core/utils/redirect';
import { COURSE_STATUS } from '@pfsc/core/models/course-status';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { ordersService } from '@pfsc/core/ui-services/orders.service';
import { useMobileDetection } from '@pfsc/core/ui-services/use-mobile-detection';
import AppDataTableServer from '../app-data-table-server.vue';
import AppDataTableServerForMobile from '../app-data-table-server-for-mobile.vue';
import StudentNameCell from '../student-name-cell.vue';
import { TABLE_FILTERS } from '../table-filters/table-filters-options';
import TableFilters from '../table-filters/table-filters.vue';
import AssignCoursesQuickActionsCell from './assign-courses-quick-actions-cell.vue';
import UpdateExpiredDateForm from './update-expired-date-form.vue';

const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },

  hasFilters: {
    type: Boolean,
    required: true,
    default: false,
  },

  hasChangeStatusButton: {
    type: Boolean,
    default: false,
  },

  hasEditableExpiredDate: {
    type: Boolean,
    default: false,
  },

  isViewInfoShown: {
    type: Boolean,
    default: false,
  },
  isExpandable: {
    type: Boolean,
    default: true,
  },

  isCheckboxShown: {
    type: Boolean,
    default: true,
  },
  fetchFunc: {
    type: Function,
    default: eLearningService.getAssignedCourses,
  },
  filtersToShown: {
    type: Array,
    default: () => [TABLE_FILTERS.courses, TABLE_FILTERS.visibility, TABLE_FILTERS.courseStatuses],
  },
  useDefaultSort: {
    type: Boolean,
    default: true,
  },
  searchPlaceholder: {
    type: String,
    default: null,
  },
  initialFilters: {
    type: Object,
    default: () => ({}),
  },
  canRowToggle: {
    type: Boolean,
    default: true,
  },
  itemKey: {
    type: String,
    default: 'id',
  },
});

const emits = defineEmits(['row-selected', 'selection-change']);

const trigger = useTriggerStore();
const authStore = useAuthStore();
const { isMobile } = useMobileDetection();
const MOBILE_CELL_COLUMN_KEY = 'confirmationId';
const mobileHeaders = computed(() => /** @type {{ key: string, title: string }[]} */ (props.headers || []));

/** @type {import('vue').Ref<import('vue').UnwrapRef<PaginationInfo>>} */
const pagination = ref({ ...DEFAULT_PAGINATION_INFO });

/** @type {import('vue').Ref<ELearningRegistrationOptions>} */
const queryParams = ref(new ELearningRegistrationOptions({
  ...(props.useDefaultSort
    ? queryParamsService.getDefaultQueryParamsWithSort()
    : queryParamsService.getDefaultQueryParams()),
  ...DEFAULT_TABLE_QUERY_PARAMS,
  ...props.initialFilters,
  groupAdmin: authStore.groupAdmin,
  learner: authStore.learner,
}));

const assigedCourseTableRef = ref(null);

const tableSort = ref(
  queryParams.value.sortBy
    ? mapModelToTableSort({
      sortBy: queryParams.value.sortBy,
      sortDirection: queryParams.value.sortDirection,
    })
    : [],
);

/** @type {import('vue').Ref<ELearning[]>} */
const selectedCourses = ref([]);
const selectedLearners = computed(() => selectedCourses.value.map(course => course.user));

const isLoading = ref(false);
const isUpdatingExpiredDate = ref(false);

const statusToReadable = {
  [COURSE_STATUS.NOT_STARTED]: globalTranslate('courseStatus.notStarted'),
  [COURSE_STATUS.IN_PROGRESS]: globalTranslate('courseStatus.inProgress'),
  [COURSE_STATUS.COMPLETED]: globalTranslate('courseStatus.completed'),
};

/**
 * Refresh assigned courses with current query params.
 * @returns {Promise<void>}
 */
function refreshAssignedCourses() {
  return toggleExecutionState(isLoading, async () => {
    pagination.value = await props.fetchFunc(queryParams.value);
  }, () => {
    isLoading.value = true;
  });
}

/**
 * Get courses .
 * @param {object} option Option
 * @param {Array} option.sortBy Sort by
 */
async function getCourses({ sortBy }) {
  tableSort.value = sortBy;
  if (sortBy) {
    queryParams.value = {
      ...queryParams.value,
      ...mapTableSortToModel(sortBy),
    };
  }
  await refreshAssignedCourses();
}

/**
 * Handle update expired date.
 * @param {Date} value Expired date.
 * @param {ELearning} eLearning E-learning
 * @returns {Promise<void>}
 */
async function onUpdateExpiredDate(value, eLearning) {
  const isSameDate = moment(value).isSame(moment(eLearning.courseInfo.expiredDate));
  if (isSameDate) {
    return;
  }

  return toggleExecutionState(isUpdatingExpiredDate, async () => {
    await eLearningService.updateExpiredDate(eLearning, {
      status: eLearning.status,
      expirationDate: value,
    });
    await refreshAssignedCourses();

    notificationService.notifyCommonSuccess();
  });
}

/**
 * Handle page change.
 * @param {object} options New options
 */
function onPageChange(options) {
  queryParams.value.page = options.page;
  queryParams.value.perPage = options.perPage;
}

/**
 * Get selected courses.
 * @returns {ELearning[]}
 */
function getSelectedCourses() {
  return selectedCourses.value;
}

/**
 * Get current query params.
 * @returns {QueryParams}
 */
function getCurrentQueryParams() {
  return queryParams.value;
}

/** Reset page and refresh list. */
function resetPageAndRefreshList() {
  queryParams.value.page = DEFAULT_QUERY_PARAMS.page;
  refreshAssignedCourses();
}

/** Handle update learner status complete. */
async function onUpdateStatusComplete() {
  await refreshAssignedCourses();
  selectedCourses.value = [];
}

/**
 * To readable training status.
 * @param {ELearning | null} course Course.
 * @returns {string}
 */
function toReadableTrainingStatus(course) {
  if (course == null) {
    return toReadableIfNull(null);
  }
  if (course.status.name === LICENSE_STATUS.CANCELLED) {
    return LicenseStatus.toReadable(course.status);
  }
  if (statusToReadable[course?.courseInfo?.status]) {
    return statusToReadable[course.courseInfo.status];
  }
  return toReadableIfNull(course?.courseInfo?.status);
}

/**
 * Emit selected row to parent component.
 * @param {object} row Selected row data.
 * @returns {void}
 */
function rowSelected(row) {
  emits('row-selected', row);
}

watch(
  () => trigger.registrationsChange,
  async () => {
    queryParams.value.page = DEFAULT_QUERY_PARAMS_WITH_SORT.page;
    await refreshAssignedCourses();
  },
);

/**
 * Emit selection changes to parent for persistent selection tracking.
 */
watch(
  selectedCourses,
  newSelection => {
    emits('selection-change', newSelection);
  },
  { deep: true },
);

onMounted(async () => {
  await getCourses({ sortBy: tableSort.value });
});

defineExpose({
  getCourses,
  refreshAssignedCourses,
  getSelectedCourses,
  getCurrentQueryParams,
  onUpdateStatusComplete,
  selectedLearners,
});
</script>
<template>
  <section :class="$style.section">
    <slot name="bulkActions" />
    <template v-if="hasFilters">
      <table-filters
        v-model="queryParams"
        :package-types="[PACKAGE_TYPE.ELearning]"
        :filters-to-shown="filtersToShown"
        :search-placeholder="searchPlaceholder"
        :disabled="isLoading"
        @update:model-value="resetPageAndRefreshList"
      />

      <v-divider />
    </template>

    <app-data-table-server
      v-if="!isMobile"
      ref="assigedCourseTableRef"
      v-model="selectedCourses"
      :headers="headers"
      :items="pagination.data"
      :page="queryParams.page"
      :per-page="queryParams.perPage"
      :sort="tableSort"
      :total-result="pagination.total"
      :total-pages="pagination.totalPages"
      show-select
      :item-key="itemKey"
      :item-value="itemKey"
      :is-items-loading="isLoading"
      :fetch-items="getCourses"
      :is-expandable="isExpandable"
      :class="[
        $style.table,
        hasFilters ? $style.assignedCoursesTable : $style.noFilters,
      ]"
      :can-row-toggle="!isViewInfoShown && canRowToggle"
      :is-checkbox-shown="isCheckboxShown"
      @on-page-change="onPageChange"
      @row-selected="rowSelected"
    >
      <template #confirmationId="{ item }">
        {{ item.raw.confirmationId }}
      </template>

      <template #createdDate="{ item }">
        {{ dateUtils.toReadable(item.raw.allocationDate) }}
      </template>

      <template #expiredDate="{ item }">
        <update-expired-date-form
          v-if="item.raw.courseInfo != null && hasEditableExpiredDate"
          :model-value="item.raw.courseInfo.expiredDate"
          @update:model-value="onUpdateExpiredDate($event, item.raw)"
        />
        <span v-else>{{ dateUtils.toReadable(item.raw?.courseInfo?.expiredDate) }}</span>
      </template>

      <template #student="{ item }">
        <student-name-cell
          :package="item.raw.package"
          :learner="item.raw.user"
          :to-tab="TAB_VALUE.STUDENT_E_LEARNING"
        />
      </template>

      <template #manager="{ item }">
        <span class="highlight">{{ getFullName(item.raw.user.manager) }}</span>
      </template>

      <template #courseName="{ item }">
        {{ toReadableIfNull(item.raw.courseInfo?.course?.name) }}
      </template>

      <template #orderItem="{ item }">
        <span>{{ item.raw.orderItem?.order?.id }}</span>
      </template>

      <template #courseProgress="{item}">
        {{ item.raw.courseInfo?.progress != null ? `${item.raw.courseInfo?.progress}%` : toReadableIfNull(item.raw.courseInfo?.progress) }}
      </template>

      <template #quickActions="{ item }">
        <assign-courses-quick-actions-cell :e-learning="item.raw" />
      </template>

      <template #trainingStatus="{ item }">
        <span class="text-capitalize">{{ toReadableTrainingStatus(item.raw) }}
          {{
            item.raw.courseInfo?.progress != null ? `(${item.raw.courseInfo.progress}%)` : toReadableIfNull(item.raw.courseInfo?.progress)
          }}
        </span>
      </template>

      <template #completedDate="{ item }">
        {{ toReadableIfNull(item.raw.courseInfo?.trainingCertificate?.createdDate) }}
      </template>

      <template #verificationCode="{ item }">
        {{ toReadableIfNull(item.raw.courseInfo?.trainingCertificate?.code) }}
      </template>

      <template #action="{ item }">
        <slot name="action" :item="item" />
      </template>

      <template v-if="isViewInfoShown" #expandIcon="{ item }">
        <router-link
          class="highlight"
          title="View"
          :to="redirectUtils.getStudentDetailUrl(authStore.currentUser, item.raw.user, TAB_VALUE.STUDENT_E_LEARNING, item.raw.id)"
        >
          <v-btn :class="[$style.expandButton]">
            {{ globalTranslate('table.viewInfo') }}
          </v-btn>
        </router-link>
        <div :class="$style.payNowButton">
          <v-btn
            v-if="item.raw.balance > 0 || item.raw?.registration?.balance > 0"
            :class="[$style.expandButton]"
            @click="ordersService.payNow(item)"
          >
            {{ globalTranslate('newOrders.payNow') }}
          </v-btn>
        </div>
      </template>
      <template #expanded="{ item, columns, isExpanded }">
        <slot
          v-if="item.raw.courseInfo != null && isExpanded.value"
          name="expandedRow"
          :item="item"
          :columns="columns"
          :is-expanded="isExpanded"
        />
      </template>
    </app-data-table-server>

    <app-data-table-server-for-mobile
      v-else
      v-model="selectedCourses"
      :class="[
        $style.table,
        hasFilters ? $style.assignedCoursesTable : $style.noFilters,
      ]"
      :mobile-cell-column-key="MOBILE_CELL_COLUMN_KEY"
      :headers="headers"
      :items="pagination.data"
      :page="queryParams.page"
      :per-page="queryParams.perPage"
      :sort="tableSort"
      :total-result="pagination.total"
      :total-pages="pagination.totalPages"
      :item-key="itemKey"
      :is-items-loading="isLoading"
      :fetch-items="getCourses"
      :can-row-toggle="!isViewInfoShown && canRowToggle"
      :is-checkbox-shown="isCheckboxShown"
      :cell-title="globalTranslate('assignment.online')"
      show-select
      @row-selected="rowSelected"
      @on-page-change="onPageChange"
    >
      <template #[`item.${MOBILE_CELL_COLUMN_KEY}`]="{ item }">
        <div :class="$style.mobileInfoCell">
          <template v-for="column in mobileHeaders" :key="column.key">
            <div :class="$style.mobileLine">
              <span :class="$style.mobileLabel">{{ column.title }}:</span>
              <template v-if="column.key === 'confirmationId'">
                <span>{{ item.raw.confirmationId }}</span>
              </template>
              <template v-else-if="column.key === 'createdDate'">
                <span>{{ dateUtils.toReadable(item.raw.allocationDate) }}</span>
              </template>
              <template v-else-if="column.key === 'expiredDate'">
                <span>{{ dateUtils.toReadable(item.raw?.courseInfo?.expiredDate) }}</span>
              </template>
              <template v-else-if="column.key === 'student'">
                <student-name-cell
                  :package="item.raw.package"
                  :learner="item.raw.user"
                  :to-tab="TAB_VALUE.STUDENT_E_LEARNING"
                />
              </template>
              <template v-else-if="column.key === 'manager'">
                <span class="highlight">{{ getFullName(item.raw.user?.manager) }}</span>
              </template>
              <template v-else-if="column.key === 'courseName'">
                <span>{{ toReadableIfNull(item.raw.courseInfo?.course?.name) }}</span>
              </template>
              <template v-else-if="column.key === 'orderItem'">
                <span>{{ item.raw.orderItem?.order?.id }}</span>
              </template>
              <template v-else-if="column.key === 'courseProgress'">
                <span>
                  {{
                    item.raw.courseInfo?.progress != null
                      ? `${item.raw.courseInfo?.progress}%`
                      : toReadableIfNull(item.raw.courseInfo?.progress)
                  }}
                </span>
              </template>
              <template v-else-if="column.key === 'quickActions'">
                <assign-courses-quick-actions-cell :e-learning="item.raw" />
              </template>
              <template v-else-if="column.key === 'trainingStatus'">
                <span class="text-capitalize">
                  {{ toReadableTrainingStatus(item.raw) }}
                  {{
                    item.raw.courseInfo?.progress != null
                      ? `(${item.raw.courseInfo.progress}%)`
                      : toReadableIfNull(item.raw.courseInfo?.progress)
                  }}
                </span>
              </template>
              <template v-else-if="column.key === 'completedDate'">
                <span>{{ toReadableIfNull(item.raw.courseInfo?.trainingCertificate?.createdDate) }}</span>
              </template>
              <template v-else-if="column.key === 'verificationCode'">
                <span>{{ toReadableIfNull(item.raw.courseInfo?.trainingCertificate?.code) }}</span>
              </template>
              <template v-else-if="column.key === 'action'">
                <slot name="action" :item="item" />
              </template>
              <template v-else>
                <span>{{ item.raw[column.key] }}</span>
              </template>
            </div>
          </template>

          <div :class="$style.mobileActions">
            <div v-if="isViewInfoShown">
              <router-link
                class="highlight"
                title="View"
                :to="redirectUtils.getStudentDetailUrl(authStore.currentUser, item.raw.user, TAB_VALUE.STUDENT_E_LEARNING, item.raw.id)"
              >
                <v-btn :class="[$style.expandButton]">
                  {{ globalTranslate('table.viewInfo') }}
                </v-btn>
              </router-link>
            </div>
            <div v-else-if="isExpandable">
              <div>
                <v-btn
                  :class="[
                    $style.expandButton,
                  ]"
                >
                  {{ globalTranslate('table.viewInfo') }}
                </v-btn>
              </div>
            </div>
            <div :class="$style.payNowButton">
              <v-btn
                v-if="item.raw.balance > 0 || item.raw?.registration?.balance > 0"
                :class="[$style.expandButton]"
                @click="ordersService.payNow(item)"
              >
                {{ globalTranslate('newOrders.payNow') }}
              </v-btn>
            </div>
          </div>
        </div>
      </template>
    </app-data-table-server-for-mobile>
  </section>
</template>

<style module>
.section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.changeStatusButton {
  align-self: flex-end;
}

.filters {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.filter {
  flex: 1;
  min-width: max-content;
}

.table {
  /* Make header columns center */
  --header-justify-content: center;
}

.table thead {
  /* Make header columns center */
  text-align: center;
  background: white;
}

.expiredDate {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 150px;
}

.assignedCoursesTable {
  height: calc(100vh - 455px);
}

.noFilters {
  height: calc(100vh - 330px);
}

.payNowButton {
  margin: 5px auto;
  border-color: var(--pfs-red);
  color: var(--pfs-red);
}

.expandButton {
  margin-top: 5px;
}

.mobileInfoCell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
}

.mobileLine {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.mobileLabel {
  font-weight: 600;
  width: 125px;
}

.mobileActions {
  display: flex;
  margin-top: 8px;
  gap: 8px;
  flex-wrap: wrap;
}

.mobileActions .payNowButton {
  margin: unset;
}

@media screen and (max-width: 1366px) {
  .assignedCoursesTable {
    height: calc(100vh - 515px);
  }
  .noFilters {
    height: calc(100vh - 390px);
  }
}

/* Is Mobile screen */
@media screen and (max-width: 1160px) {
  .table thead {
    text-align: left;
  }
}

@media screen and (max-width: 1104px) {
  .assignedCoursesTable {
    height: calc(100vh - 570px);
  }
  .noFilters {
    height: calc(100vh - 445px);
  }
}

@media screen and (max-width: 844px) {
  .assignedCoursesTable {
    height: calc(100vh - 620px);
  }
  .noFilters {
    height: calc(100vh - 495px);
  }
}

@media screen and (max-width: 600px) {
  .assignedCoursesTable {
    min-height: 447px;
  }
}

@media screen and (max-height: 650px) {
  .assignedCoursesTable {
    min-height: 447px;
  }
}
</style>
